import {
  faArrowRight,
  faCircleInfo,
  faEllipsis,
  faMinus,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Grid,
  Group,
  Input,
  Menu,
  Stack,
  Table,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  useDeleteMutation,
  useInsertMutation,
} from "@supabase-cache-helpers/postgrest-react-query";
import { PostgrestError } from "@supabase/postgrest-js";
import {
  ActionIconLink,
  ButtonLink,
  MenuItemLink,
} from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useMemo } from "react";
import { useCallback, useState } from "react";

import { api } from "@/api.ts";
import {
  TransactionsTable,
  transactionsQuerySchema,
} from "@/components/TransactionsTable.tsx";
import { useCatalogItems } from "@/hooks/useCatalogItems.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/projects/$identifier")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Project`,
      },
    ],
  }),
  validateSearch: transactionsQuerySchema,
});

const DatasetMenu: FC<{ identifier: string; uri: string }> = ({
  identifier,
  uri,
}) => {
  const { t } = useLingui();

  const catalog = useCatalogItems();
  const record = useMemo(() => catalog.forUri(uri), [catalog, uri]);
  if (!record) {
    return null;
  }

  const linkStac = record.links.find(
    (l) => l.rel === "service" && l.profile?.includes("https://stacspec.org"),
  );
  const linkOgcApiFeatures = record.links.find(
    (l) =>
      l.rel === "service" &&
      l.profile?.includes(
        "http://www.opengis.net/def/interface/ogcapi-features",
      ),
  );

  return (
    <Menu>
      <Menu.Target>
        <ActionIcon color="green" variant="light">
          <FontAwesomeIcon icon={faEllipsis} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <Trans>Download</Trans>
        </Menu.Label>
        {linkStac && (
          <Menu.Item
            component="a"
            href={`/stac/browser/${new URL(linkStac.href).pathname.replace("/stac/", "")}`}
            target="_blank"
          >
            <Trans>Go to STAC Browser</Trans>
          </Menu.Item>
        )}
        <MenuItemLink
          to="/flows/download/selection/$identifier"
          params={{ identifier: identifier }}
          search={{ datasets: [uri] }}
          disabled={
            !record.links.some(
              (l) =>
                l.rel === "http://www.opengis.net/def/rel/ogc/1.0/execute" &&
                l.href.endsWith("/processes/extraction/execution"),
            )
          }
        >
          <Trans>Download data</Trans>
        </MenuItemLink>
        {linkStac && (
          <Menu.Item
            onClick={async () => {
              await navigator.clipboard.writeText(linkStac.href);
              notifications.show({
                title: t`Copied to clipboard`,
                message: t`STAC URL copied to clipboard`,
              });
            }}
          >
            <Trans>Copy STAC URL</Trans>
          </Menu.Item>
        )}

        <Menu.Divider />

        <Menu.Label>
          <Trans>Tiles</Trans>
        </Menu.Label>
        <Menu.Item disabled>
          <Trans>Copy WMS URL</Trans>
        </Menu.Item>
        <Menu.Item disabled>
          <Trans>Copy WMTS URL</Trans>
        </Menu.Item>

        <Menu.Divider />

        <Menu.Label>API</Menu.Label>
        {linkOgcApiFeatures && (
          <Menu.Item
            onClick={async () => {
              await navigator.clipboard.writeText(linkOgcApiFeatures.href);
              notifications.show({
                title: t`Copied to clipboard`,
                message: t`OGC API Features URL copied to clipboard`,
              });
            }}
          >
            <Trans>Copy OGC API Features URL</Trans>
          </Menu.Item>
        )}

        <Menu.Divider />

        <Menu.Label>
          <Trans>Documentation</Trans>
        </Menu.Label>
        {record.links
          .filter(
            (l) =>
              l.rel.toLowerCase() === "describedby" &&
              ["text/html", "application/pdf"].includes(l.type ?? ""),
          )
          .map((l, idx) => (
            <Menu.Item key={idx}>{l.title}</Menu.Item>
          ))}
      </Menu.Dropdown>
    </Menu>
  );
};

const ProjectDatasets: FC<{
  id: number;
  identifier: string;
  hasAccess: boolean;
}> = ({ id, identifier, hasAccess }) => {
  const { data } = useSuspenseQueryPostgrest(
    api.from("project_dataset").select("*").eq("project_id", id),
  );
  const catalog = useCatalogItems();

  return (
    <>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Trans>Dataset</Trans>
            </Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.length === 0 && (
            <Table.Tr>
              <Table.Td
                colSpan={2}
                align="center"
                style={{ paddingTop: 20, paddingBottom: 20 }}
              >
                <Trans>Empty</Trans>
              </Table.Td>
            </Table.Tr>
          )}
          {data.map((member) => (
            <Table.Tr key={member.dataset_uri}>
              <Table.Td>
                {catalog.forUri(member.dataset_uri)?.properties.title}
              </Table.Td>
              <Table.Td>
                {hasAccess && (
                  <DatasetMenu
                    identifier={identifier}
                    uri={member.dataset_uri}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {hasAccess && (
        <ButtonLink
          to="/flows/authorize/$identifier"
          params={{ identifier }}
          color="green"
          variant="light"
        >
          <Trans>Add dataset</Trans>
        </ButtonLink>
      )}
    </>
  );
};
const ProjectMembers: FC<{ id: number; hasAccess: boolean }> = ({
  id,
  hasAccess,
}) => {
  const { t } = useLingui();

  const { data } = useSuspenseQueryPostgrest(
    api.from("project_member").select("*").eq("project_id", id),
  );

  const [addUserEmail, setAddUserEmail] = useState("");
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const { mutateAsync: insert, isPending: insertLoading } = useInsertMutation(
    api.from("project_member"),
    ["project_id", "user"],
    "*",
  );

  const onAddMember = useCallback(async () => {
    try {
      setAddUserError(null);
      await insert([{ project_id: id, user: addUserEmail }]);
      setAddUserEmail("");
    } catch (e) {
      let errorMessage: string | null = null;
      if (e instanceof PostgrestError) {
        if (e.code === "23505") {
          // Duplicate key
          errorMessage = t`This user is already added to the project`;
        } else if (e.code === "42501") {
          // Insufficient privileges
          errorMessage = t`You are not allowed to add this user to the project`;
        } else {
          errorMessage = e.message;
        }
      } else {
        errorMessage = (e as Error).message;
      }
      setAddUserError(errorMessage);
    }
  }, [addUserEmail, id, insert, t]);

  const { mutateAsync: deleteMember } = useDeleteMutation(
    api.from("project_member"),
    ["project_id", "user"],
    "*",
  );

  return (
    <>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Trans>User</Trans>
            </Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((member) => (
            <Table.Tr key={member.user}>
              <Table.Td>{member.user}</Table.Td>
              <Table.Td>
                {hasAccess && (
                  <ActionIcon
                    onClick={() =>
                      deleteMember({ project_id: id, user: member.user })
                    }
                    disabled={data.length === 1}
                    title={
                      data.length === 1 ? t`Cannot remove last user` : undefined
                    }
                    color="green"
                    variant="light"
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </ActionIcon>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {hasAccess && (
        <Input
          placeholder={t`Add user by email`}
          value={addUserEmail}
          onChange={(e) => setAddUserEmail(e.target.value)}
          rightSectionPointerEvents="auto"
          rightSection={
            <ActionIcon
              onClick={onAddMember}
              loading={insertLoading}
              color="green"
              variant="transparent"
            >
              <FontAwesomeIcon icon={faPlus} />
            </ActionIcon>
          }
        />
      )}
      {addUserError && (
        <Input.Error style={{ marginTop: ".5rem" }}>{addUserError}</Input.Error>
      )}
    </>
  );
};

function RouteComponent() {
  const { data } = useSuspenseQueryPostgrest(
    api
      .from("project")
      .select("*,is_member,customer(id,name)")
      .eq("identifier", Route.useParams().identifier)
      .single(),
  );

  const { t } = useLingui();

  return (
    <Grid>
      <Grid.Col span={4}>
        <h3>
          <Trans>Datasets</Trans>
        </h3>
        <ProjectDatasets
          id={data.id}
          identifier={data.identifier}
          hasAccess={data.is_member}
        />
      </Grid.Col>
      <Grid.Col span={4}>
        <h3>
          <Trans>Members</Trans>
        </h3>
        <ProjectMembers id={data.id} hasAccess={data.is_member} />
      </Grid.Col>
      <Grid.Col span={4}>
        <h3>
          <Trans>Settings</Trans>
        </h3>
        <Stack gap="sm">
          <TextInput
            label={t`Identifier`}
            value={data.identifier}
            readOnly
            disabled
          />
          <TextInput label={t`Name`} value={data.name} readOnly disabled />
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label={t`Start`}
                value={data.start}
                readOnly
                disabled
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label={t`End`} value={data.end} readOnly disabled />
            </Grid.Col>
          </Grid>
          <TextInput
            label={t`Customer`}
            value={data.customer?.name}
            readOnly
            disabled
            rightSection={
              data.customer ? (
                <ActionIconLink
                  to="/customers/$id"
                  params={{ id: String(data.customer.id) }}
                  variant="light"
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </ActionIconLink>
              ) : null
            }
          />
          {data.customer_domain && (
            <TextInput
              label={
                <Group gap="sm">
                  <Trans>Customer User Domain</Trans>
                  <Tooltip
                    label={t`Users with this e-mail domain will be charged to this project`}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      color="var(--mantine-color-gray-filled)"
                    />
                  </Tooltip>
                </Group>
              }
              value={data.customer_domain}
              readOnly
              disabled
            />
          )}
        </Stack>
      </Grid.Col>
      <Grid.Col span={12}>
        <h3>
          <Trans>Transactions</Trans>
        </h3>
        <TransactionsTable
          fixedValues={{ project: data.id }}
          route={Route.id}
        />
      </Grid.Col>
    </Grid>
  );
}
